import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type StatusBadgeVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

interface Props {
  label: string
  variant?: StatusBadgeVariant
  className?: string
  /** Opcjonalna ikona (np. lucide z size-3) z lewej strony etykiety. */
  prefix?: ReactNode
  /** Tooltip wyswietlany po najechaniu na badge. */
  title?: string
}

const VARIANTS: Record<StatusBadgeVariant, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  info:    'bg-sky-100 text-sky-700',
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  danger:  'bg-red-100 text-red-700',
}

/**
 * Spójny badge statusu używany w klientach (active/inactive), umowach (per etap z
 * kolorem z backendu), taskach i wszędzie, gdzie potrzebny jest read-only chip statusu.
 *
 * 5 wariantów semantycznych:
 *   - neutral  -- zarezerwowane, draft, brak specjalnego znaczenia
 *   - info     -- w toku, aktywne
 *   - success  -- zakończone pomyślnie
 *   - warning  -- oczekuje na akcję, wersja robocza wymagająca uwagi
 *   - danger   -- anulowane, odrzucone, błąd
 */
export function StatusBadge({ label, variant = 'neutral', className, prefix, title }: Props) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium',
        VARIANTS[variant],
        className,
      )}
    >
      {prefix}
      {label}
    </span>
  )
}
